import React, { useRef, useEffect, useState } from 'react';

import { ResizeObserver } from '@juggle/resize-observer';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';

import { isContentOverflowingElement } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import Tooltip from '../Tooltip';

type OverflowTooltipProps = Omit<
  React.ComponentProps<typeof Tooltip>,
  'content'
> & {
  autoTipOnEllipsis?: boolean;
  singleLine?: boolean;
  lineClamp?: number;
  content?: React.ReactNode;
};

/**
 * Tooltip component display on hover of message with ellipsis.
 */
const OverflowTooltip = ({
  children,
  content,
  autoTipOnEllipsis = true,
  singleLine = true,
  lineClamp = 2,
  getTargetProps = () => ({ style: { width: '100%' } }),
  ...props
}: OverflowTooltipProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const showProps = !isTruncated ? { show: false } : {};

  useEffect(() => {
    let resizeObserverEntries: ReadonlyArray<ResizeObserverEntry> = [];
    const checkIfOverflowing = () =>
      setIsTruncated(isContentOverflowingElement(ref));

    // Check if overflowing on initial mount
    checkIfOverflowing();

    // Check if overflowing when target's dimension changes
    const resizeObserver = new ResizeObserver((entries) => {
      debounce(checkIfOverflowing, 100);
      resizeObserverEntries = entries;
    });

    if (ref.current) resizeObserver.observe(ref.current as HTMLElement);

    // Check if overflowing when target's text content changes
    const mutationObserver = new MutationObserver(
      debounce(checkIfOverflowing, 100),
    );
    mutationObserver.observe(ref.current as HTMLElement, {
      subtree: true,
      characterData: true,
    });

    return () => {
      resizeObserverEntries.forEach((entry) => entry.target.remove());
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return (
    <Tooltip
      content={content || children}
      placement={Tooltip.PLACEMENTS.TOP}
      getTargetProps={getTargetProps}
      {...showProps}
      {...props}
    >
      <div
        ref={ref}
        css={[
          singleLine && autoTipOnEllipsis && theme.typography.ellipsis,

          !singleLine &&
            autoTipOnEllipsis && {
              display: '-webkit-box',
              WebkitLineClamp: lineClamp,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal',
            },

          !singleLine &&
            !autoTipOnEllipsis && {
              display: 'inline',
              whiteSpace: 'normal',
            },
        ]}
      >
        {children}
      </div>
    </Tooltip>
  );
};

OverflowTooltip.propTypes = {
  ...Tooltip.propTypes,
  /**
   * className to add to the Tooltip component
   */
  className: PropTypes.string,
  /**
   * style to add to the Tooltip component
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * This would be the content if supplied, otherwise it'll use `children` as the content inside the tooltip.
   */
  content: PropTypes.node,
  /**
   * whether to show ellipsis on overflow
   */
  autoTipOnEllipsis: PropTypes.bool,
  /**
   * whether the content should be limited to a single line
   */
  singleLine: PropTypes.bool,
  /**
   * number of lines we want to show using line clamp
   */
  lineClamp: PropTypes.number,
  /**
   * children node or function
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

export default Object.assign(OverflowTooltip, {
  VARIANTS: Tooltip.VARIANTS,
  PLACEMENTS: Tooltip.PLACEMENTS,
  TRIGGER_EVENTS: Tooltip.TRIGGER_EVENTS,
  TRIGGER_TARGETS: Tooltip.TRIGGER_TARGETS,
});
